<template>
    <div class="space-y-3">
        <section class="space-y-1">
            <form-label>GPIO entity</form-label>
            <div v-if="source" class="flex items-center justify-between gap-2 text-xs">
                <button class="truncate text-left hover:text-ui-active" title="Select the GPIO entity node"
                    @click="logic.select(page, 'node', source.id)">GPIO {{ source.data.entity || '(no entity selected)' }}</button>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the GPIO entity"
                    @click="logic.removeWire(page, sourceWire.id)">×</button>
            </div>
            <p v-else class="text-xs text-gray-300">Drag a wire from a GPIO entity's State to this binding.</p>
        </section>

        <form-select name="GPIO property" :model-value="node.data.property" @update:model-value="setProperty">
            <option value="" disabled>Select a property</option>
            <option v-if="node.data.property && !properties.includes(node.data.property)" :value="node.data.property">{{ node.data.property }} · saved</option>
            <option v-for="property in properties" :key="property" :value="property">{{ property }}</option>
        </form-select>
        <form-select name="Target property" :model-value="node.data.target_property" @update:model-value="setTarget">
            <option value="" disabled>Select a target</option>
            <option v-if="node.data.target_property && !targetProperties.includes(node.data.target_property)" :value="node.data.target_property">{{ node.data.target_property }} · saved</option>
            <option v-for="target in targetProperties" :key="target" :value="target">{{ target }}</option>
        </form-select>

        <section v-if="showMapping" class="space-y-1">
            <form-label>Mapping</form-label>
            <div class="grid grid-cols-2 gap-2">
                <BuilderInput v-for="key in RANGE_KEYS" :key="key" :name="RANGE_LABELS[key]" type="number"
                    :model-value="node.data[key] ?? fallback(key)" @update:model-value="value => setRange(key, value)" />
            </div>
        </section>

        <section class="space-y-1">
            <form-label>Drives</form-label>
            <p v-if="!targets.length" class="text-xs text-gray-300">Drag a wire from Value to the left edge of an object.</p>
            <div v-for="item in targets" :key="item.wire.id" class="flex items-center justify-between gap-2 text-xs">
                <span class="truncate">{{ item.label }}</span>
                <button class="text-red-300 hover:text-red-200" title="Disconnect the object" @click="logic.removeWire(page, item.wire.id)">×</button>
            </div>
        </section>
        <p class="text-xs text-gray-400">Exported as a GPIO State Binding of each object it drives.</p>
    </div>
</template>

<script setup>
import { computed, toRaw } from 'vue';
import { useLogicStore } from '../../stores/logicStore.js';
import { getBindingSource } from '../../logic/logicGraph.js';
import {
    availableEntityProperties,
    mappingFallback,
    mappingVisible,
    statePropertyOptions,
    stateTargetOptions,
    stateBindingRanges,
} from '../../gpio/gpioEditorModel.js';
import BuilderInput from '../hassbuilder/automation/BuilderInput.vue';

const RANGE_KEYS = ['in_min', 'in_max', 'out_min', 'out_max'];
const RANGE_LABELS = { in_min: 'GPIO min', in_max: 'GPIO max', out_min: 'Object min', out_max: 'Object max' };

/** A GPIO binding node: the GPIO property it reads, the object property it drives, and its mapping. */
const props = defineProps({
    page: { type: Object, required: true },
    graph: { type: Object, required: true },
    node: { type: Object, required: true },
    context: { type: Object, required: true },
});
const logic = useLogicStore();

const sourceWire = computed(() => props.graph.wires.find(wire => wire.to.node === props.node.id && wire.to.port === 'source') ?? null);
const source = computed(() => getBindingSource(props.graph, props.node.id));
const entity = computed(() => props.context.getGpioEntity?.(source.value?.data?.entity) ?? null);
const targets = computed(() => props.graph.wires
    .filter(wire => wire.from.node === props.node.id && wire.from.port === 'value' && wire.to.object !== undefined)
    .map(wire => ({ wire, object: props.context.getObject(wire.to.object) }))
    .filter(item => item.object)
    .map(item => ({ ...item, label: `${item.object.obj} p${props.page.haspid}b${item.object.haspid}${item.object.comment ? ` — ${item.object.comment}` : ''}` })));
const firstTarget = computed(() => targets.value[0]?.object ?? null);

// Shared by every driven object (all of them when none is wired yet).
const shared = lists => (lists.length ? lists.reduce((common, list) => common.filter(item => list.includes(item))) : []);
const properties = computed(() => {
    const objects = targets.value.map(item => item.object);
    const byObject = objects.map(object => availableEntityProperties(entity.value, { mode: 'state', objectType: object.obj })
        .filter(property => statePropertyOptions(object.obj).includes(property)));
    return objects.length ? shared(byObject) : availableEntityProperties(entity.value, { mode: 'state' });
});
const targetProperties = computed(() => shared(targets.value
    .map(item => stateTargetOptions(item.object.obj, item.object, props.node.data.property))));
const showMapping = computed(() => Boolean(firstTarget.value) && mappingVisible(props.node.data, 'state', firstTarget.value.obj));

const fallback = key => mappingFallback(props.node.data, key, 'state', firstTarget.value, entity.value);

function save(data) {
    logic.setNodeData(props.page, props.node.id, data);
}

// A new property or target starts with the default mapping for the first driven object.
function withRanges(data) {
    const plain = Object.fromEntries(Object.entries(data).filter(([key]) => !RANGE_KEYS.includes(key)));
    return { ...plain, ...stateBindingRanges(firstTarget.value, entity.value, plain.property, plain.target_property) };
}

function setProperty(property) {
    const data = { ...toRaw(props.node).data, property };
    const options = firstTarget.value ? stateTargetOptions(firstTarget.value.obj, firstTarget.value, property) : [];
    if (!options.includes(data.target_property)) data.target_property = options[0] ?? '';
    save(withRanges(data));
}

function setTarget(target_property) {
    save(withRanges({ ...toRaw(props.node).data, target_property }));
}

function setRange(key, value) {
    const number = Number(value);
    save({ ...toRaw(props.node).data, [key]: Number.isFinite(number) ? Math.trunc(number) : undefined });
}
</script>
